import { onMounted, onUnmounted } from 'vue'

export interface Shortcut {
  /** Touche principale (insensible à la casse), ex. 'm', 'Escape', ' '. */
  key: string
  /** Si vrai, exige Alt. Par défaut : Alt = false. */
  alt?: boolean
  /** Si vrai, exige Ctrl ou Cmd (⌘ macOS). */
  ctrl?: boolean
  /** Si vrai, exige Shift. */
  shift?: boolean
  /** Handler appelé quand la combinaison matche. */
  handler: (ev: KeyboardEvent) => void
  /** Si vrai (défaut), saute le shortcut quand l'utilisateur tape dans
   *  un input/textarea/contenteditable. */
  skipInInputs?: boolean
  /** Description courte — utile pour générer une aide. */
  description?: string
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

/**
 * Branche un ensemble de raccourcis clavier sur la fenêtre.
 *
 * - Combinaison Ctrl+X et ⌘+X sont équivalentes (cross-OS).
 * - Par défaut, ignore les touches quand l'utilisateur tape dans un input.
 *   Désactiver avec `skipInInputs: false` pour Escape par exemple.
 */
export function useKeyboardShortcuts(shortcuts: Shortcut[]) {
  function handler(ev: KeyboardEvent) {
    for (const s of shortcuts) {
      if (ev.key.toLowerCase() !== s.key.toLowerCase()) continue
      if (Boolean(ev.altKey) !== Boolean(s.alt)) continue
      if (Boolean(ev.ctrlKey || ev.metaKey) !== Boolean(s.ctrl)) continue
      if (Boolean(ev.shiftKey) !== Boolean(s.shift)) continue
      // Si le user tape dans un input, on saute (sauf si shortcut le précise)
      const skip = s.skipInInputs ?? true
      if (skip && isEditableTarget(ev.target)) continue
      ev.preventDefault()
      s.handler(ev)
      // On ne break PAS — un même évènement pourrait théoriquement matcher
      // plusieurs handlers (rare), mais ça permet de logger / inspecter.
      return
    }
  }
  onMounted(() => window.addEventListener('keydown', handler))
  onUnmounted(() => window.removeEventListener('keydown', handler))
}
